import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { localizedUrl } from "../lib/fetchLocalized";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarDays,
  HeartPulse,
  Mail,
  Search,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { fallbackBlogs } from "../data/fallbackBlogs";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import PageBanner from "../components/PageBanner";

const editorialTrackImages = [
  "/assets/agm/swamiji-vedant.jpg",
  "/assets/agm/mahakumbh-seva.jpg",
  "/assets/agm/ifs-students.jpg",
];

const filters = ["All", "USA", "Vedant", "Healthcare", "Education", "Relief", "Women dignity"];

const Blogs = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { siteConfig } = useRegionalExperience();

  const editorialTracks = [
    {
      label: t("blogs.tracks.vedant.label"),
      text: t("blogs.tracks.vedant.text"),
      image: editorialTrackImages[0],
    },
    {
      label: t("blogs.tracks.fieldImpact.label"),
      text: t("blogs.tracks.fieldImpact.text"),
      image: editorialTrackImages[1],
    },
    {
      label: t("blogs.tracks.youth.label"),
      text: t("blogs.tracks.youth.text"),
      image: editorialTrackImages[2],
    },
  ];

  const filterLabels = {
    All: t("blogs.filters.all"),
    USA: t("blogs.filters.usa"),
    Vedant: t("blogs.filters.vedant"),
    Healthcare: t("blogs.filters.healthcare"),
    Education: t("blogs.filters.education"),
    Relief: t("blogs.filters.relief"),
    "Women dignity": t("blogs.filters.womenDignity"),
  };
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilter, setActiveFilter] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchBlogs();
  }, [currentPage]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const baseUrl = import.meta.env.VITE_APP_BASE_URL;

      if (!baseUrl) {
        setBlogs(fallbackBlogs);
        setTotalPages(1);
        return;
      }

      const response = await axios.get(
        localizedUrl(`${baseUrl}/blogs?status=approved&page=${currentPage}&limit=9`)
      );

      if (response.data.success) {
        const remoteBlogs = response.data.blogs || [];
        setBlogs(remoteBlogs.length ? remoteBlogs : fallbackBlogs);
        setTotalPages(remoteBlogs.length ? response.data.pagination?.totalPages || 1 : 1);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogs(fallbackBlogs);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("blogs.defaultDate");
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const stripHtml = (html = "") => {
    const parser = new DOMParser();
    return parser.parseFromString(html, "text/html").body.textContent || "";
  };

  const filteredBlogs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return blogs.filter((blog) => {
      const text =
        `${blog.title || ""} ${blog.deck || ""} ${stripHtml(blog.content)} ${(blog.tags || []).join(" ")}`.toLowerCase();
      const matchesQuery = !normalizedQuery || text.includes(normalizedQuery);
      const matchesFilter =
        activeFilter === "All" ||
        text.includes(activeFilter.toLowerCase()) ||
        (blog.category || "").toLowerCase() === activeFilter.toLowerCase() ||
        (blog.tags || []).some((tag) => tag.toLowerCase() === activeFilter.toLowerCase());

      return matchesQuery && matchesFilter;
    });
  }, [activeFilter, blogs, query]);

  const featuredBlog = filteredBlogs[0];
  const articleGrid = featuredBlog ? filteredBlogs.slice(1) : filteredBlogs;

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#f7faf9] text-[#14201f]">
      <PageBanner
        {...(siteConfig.blogsPage?.banner || {})}
        rightSlot={
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {[
              [t("blogs.bannerPrograms.title"), t("blogs.bannerPrograms.text")],
              [t("blogs.bannerVedant.title"), t("blogs.bannerVedant.text")],
              [t("blogs.bannerTransparency.title"), t("blogs.bannerTransparency.text")],
            ].map(([title, text]) => (
              <div
                key={title}
                className="rounded-lg border border-white/18 bg-white/10 p-5 backdrop-blur"
              >
                <BookOpenCheck className="h-6 w-6 text-[#f5c45e]" />
                <h2 className="mt-4 text-lg font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-white/76">{text}</p>
              </div>
            ))}
          </div>
        }
      />

      <section className="border-b border-[#dfe8e6] bg-white py-10">
        <div className="container mx-auto grid gap-5 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
          {editorialTracks.map((track) => (
            <article
              key={track.label}
              className="overflow-hidden rounded-lg border border-[#dfe8e6] bg-white"
            >
              <img src={track.image} alt={track.label} className="h-44 w-full object-cover" />
              <div className="p-5">
                <h2 className="text-lg font-semibold text-[#14201f]">{track.label}</h2>
                <p className="mt-3 text-sm leading-6 text-[#52615f]">{track.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="latest" className="py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#9d1f1c]">
                {t("blogs.eyebrow")}
              </p>
              <h2 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">
                {t("blogs.sectionTitle")}
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-[#52615f]">
                {t("blogs.sectionDescription")}
              </p>
            </div>

            <div className="w-full max-w-md rounded-lg border border-[#dfe8e6] bg-white p-3">
              <div className="flex items-center gap-3 rounded-md bg-[#f1f6f4] px-4 py-3">
                <Search className="h-5 w-5 text-[#60706d]" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("blogs.searchPlaceholder")}
                  className="w-full bg-transparent text-sm text-[#14201f] outline-none placeholder:text-[#60706d]"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={`rounded-md border px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] transition ${
                  activeFilter === filter
                    ? "border-[#9d1f1c] bg-[#9d1f1c] text-white"
                    : "border-[#dfe8e6] bg-white text-[#52615f] hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
                }`}
              >
                {filterLabels[filter]}
              </button>
            ))}
          </div>

          {loading && blogs.length === 0 ? (
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-96 animate-pulse rounded-lg border border-[#dfe8e6] bg-white"
                />
              ))}
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="mt-12 rounded-lg border border-[#dfe8e6] bg-white p-10 text-center">
              <HeartPulse className="mx-auto h-10 w-10 text-[#9d1f1c]" />
              <h3 className="mt-5 text-2xl font-semibold text-[#14201f]">
                {t("blogs.emptyTitle")}
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-[#52615f]">
                {t("blogs.emptyDescription")}
              </p>
              <Link
                to="/reports"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#4b2418] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white"
              >
                {t("blogs.viewAnnualReports")}
                <ArrowRight size={17} />
              </Link>
            </div>
          ) : (
            <>
              {featuredBlog && (
                <article className="mt-12 overflow-hidden rounded-lg border border-[#d8c7ba] bg-white shadow-xl shadow-[#4b2418]/10 lg:grid lg:grid-cols-[0.58fr_0.42fr]">
                  <Link
                    to={`/blogs/${featuredBlog._id}`}
                    className="block min-h-[420px] overflow-hidden"
                  >
                    <img
                      src={featuredBlog.images?.[0] || "/assets/agm/swamiji-vedant.jpg"}
                      alt={featuredBlog.title}
                      className="h-full w-full object-cover transition duration-700 hover:scale-105"
                    />
                  </Link>
                  <div className="flex flex-col justify-center p-7 md:p-10">
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-md bg-[#7f1916] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-white">
                        {t("blogs.featured")}
                      </span>
                      <span className="rounded-md bg-[#f4e4d0] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#4b2418]">
                        {featuredBlog.category || t("blogs.defaultCategory")}
                      </span>
                      {featuredBlog.sourceLinks?.length > 0 && (
                        <span className="rounded-md bg-[#f4e4d0] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#4b2418]">
                          {featuredBlog.sourceLinks.length} {t("blogs.publicSources")}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-6 text-3xl font-semibold leading-tight text-[#241511] md:text-5xl">
                      <Link to={`/blogs/${featuredBlog._id}`} className="hover:text-[#7f1916]">
                        {featuredBlog.title}
                      </Link>
                    </h3>
                    <p className="mt-5 text-base leading-8 text-[#5f4a3e]">
                      {featuredBlog.deck || stripHtml(featuredBlog.content).slice(0, 220)}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#7d6a5f]">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays size={14} />
                        {formatDate(featuredBlog.createdAt)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <User size={14} />
                        {featuredBlog.author?.username || t("blogs.defaultAuthor")}
                      </span>
                      {featuredBlog.readTime && <span>{featuredBlog.readTime}</span>}
                    </div>
                    <Link
                      to={`/blogs/${featuredBlog._id}`}
                      className="mt-8 inline-flex w-fit items-center gap-2 rounded-md bg-[#7f1916] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#641210]"
                    >
                      {t("blogs.readFullArticle")}
                      <ArrowRight size={17} />
                    </Link>
                  </div>
                </article>
              )}

              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {articleGrid.map((blog) => {
                  const excerpt = blog.deck || stripHtml(blog.content).slice(0, 170);
                  const image = blog.images?.[0] || "/assets/agm/polyclinic-consultation.jpg";

                  return (
                    <article
                      key={blog._id}
                      className="group flex overflow-hidden rounded-lg border border-[#dfe8e6] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                    >
                      <div className="flex w-full flex-col">
                        <Link
                          to={`/blogs/${blog._id}`}
                          className="block aspect-[16/10] overflow-hidden"
                        >
                          <img
                            src={image}
                            alt={blog.title}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        </Link>
                        <div className="flex flex-1 flex-col p-6">
                          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#60706d]">
                            {blog.category && (
                              <span className="rounded bg-[#f4e4d0] px-2 py-1 text-[#7f1916]">
                                {blog.category}
                              </span>
                            )}
                            {blog.sourceLinks?.length > 0 && (
                              <span className="rounded bg-[#f4e4d0] px-2 py-1 text-[#7f1916]">
                                {t("blogs.sourced")}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays size={14} />
                              {formatDate(blog.createdAt)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <User size={14} />
                              {blog.author?.username || t("blogs.defaultAuthor")}
                            </span>
                          </div>

                          <h3 className="mt-4 text-xl font-semibold leading-snug text-[#14201f]">
                            <Link to={`/blogs/${blog._id}`} className="hover:text-[#9d1f1c]">
                              {blog.title}
                            </Link>
                          </h3>
                          <p className="mt-3 flex-1 text-sm leading-7 text-[#52615f]">
                            {excerpt}
                            {excerpt.length >= 170 ? "..." : ""}
                          </p>

                          {blog.keyPoints?.length > 0 && (
                            <ul className="mt-4 space-y-2 border-l-2 border-[#7f1916] pl-4">
                              {blog.keyPoints.slice(0, 2).map((point) => (
                                <li key={point} className="text-xs leading-5 text-[#5f4a3e]">
                                  {point}
                                </li>
                              ))}
                            </ul>
                          )}

                          {blog.author?.email && (
                            <a
                              href={`mailto:${blog.author.email}`}
                              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c]"
                            >
                              <Mail size={16} />
                              {blog.author.email}
                            </a>
                          )}

                          <Link
                            to={`/blogs/${blog._id}`}
                            className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#9d1f1c]"
                          >
                            {t("blogs.readStory")}
                            <ArrowRight size={17} />
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-12 flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="rounded-md border border-[#dfe8e6] bg-white px-4 py-3 text-sm font-semibold text-[#14201f] disabled:opacity-45"
                  >
                    {t("common.previous")}
                  </button>
                  <span className="rounded-md bg-[#4b2418] px-4 py-3 text-sm font-bold text-white">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-md border border-[#dfe8e6] bg-white px-4 py-3 text-sm font-semibold text-[#14201f] disabled:opacity-45"
                  >
                    {t("common.next")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="bg-[linear-gradient(135deg,#2a120d_0%,#4b2418_56%,#7f1916_100%)] py-14 text-white">
        <div className="container mx-auto flex flex-col gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#f5c45e]">
              {t("blogs.contributorEyebrow")}
            </p>
            <h2 className="mt-3 text-3xl font-semibold">{t("blogs.contributorTitle")}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-white/72">
              {t("blogs.contributorDescription")}
            </p>
          </div>
          <Link
            to={isAuthenticated ? "/create-blog" : "/login"}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#f5c45e] px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#201711]"
          >
            {isAuthenticated ? t("blogs.writeNow") : t("blogs.loginToWrite")}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Blogs;
